import { useMemo, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

import RecordSection from './RecordSection';
import { GUEST_SECONDARY_BUTTON_SX, GUEST_TEXT_BUTTON_SX } from './guestTableStyles';
import {
  LINK_RELATIONS,
  LINK_RELATION_LABELS,
  LINK_SEATINGS,
  LINK_SEATING_LABELS,
  defaultSeatingFor,
  linkCandidates,
  linksForGuest,
  type LinkRelation,
  type LinkSeating,
} from '../../../lib/guestLinks';
import type { GuestLink, TrackerGuest } from '../../../services/tracker-api-client';

export interface NewGuestLink {
  otherId: string;
  relation: LinkRelation;
  seating: LinkSeating;
}

/**
 * Who this guest is bonded to, and what the seater must do about it.
 *
 * The picker filters the roster the page already holds rather than asking the
 * server — there is no `/invitees/search`, deliberately, because a second
 * matching surface is how two matching rules come to disagree and duplicate
 * guests get created.
 *
 * "Keep apart" is the reason this tile exists at all. It is the one thing a
 * host knows that no tag can express, and it is the most expensive thing to
 * find out on the night.
 */
export default function GuestLinksTile({
  guest,
  roster,
  links,
  busy,
  onCreate,
  onDelete,
}: {
  guest: TrackerGuest;
  roster: TrackerGuest[];
  links: GuestLink[];
  busy: boolean;
  /**
   * Resolves false when the write was refused. The form used to fold away the
   * moment the button was pressed, so a refused "keep apart" — the most
   * expensive fact a host holds — left no row, no message and nothing to
   * retype. It stays open until the link actually exists.
   */
  onCreate: (link: NewGuestLink) => Promise<boolean>;
  onDelete: (linkId: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [otherId, setOtherId] = useState('');
  const [relation, setRelation] = useState<LinkRelation>('partner');
  const [seating, setSeating] = useState<LinkSeating>(defaultSeatingFor('partner'));

  /*
    Both of these walk the WHOLE roster — `linksForGuest` builds a name map of
    every guest on the trip, `linkCandidates` localeCompare-sorts every name —
    and this tile re-renders whenever anything above it does, including on every
    keystroke in the roster search box. At 500 guests that is two full passes
    per character for a list that only changes when a link is written or a name
    is typed into the picker below.
  */
  const held = useMemo(
    () => linksForGuest(links, guest.inviteeId, roster),
    [links, guest.inviteeId, roster],
  );
  const candidates = useMemo(
    () => linkCandidates(roster, links, guest.inviteeId, query).slice(0, 30),
    [roster, links, guest.inviteeId, query],
  );

  const reset = () => {
    setAdding(false);
    setQuery('');
    setOtherId('');
  };

  return (
    <RecordSection
      title="Linked with"
      description="Partners, households, and the two people who must not share a table."
    >
      <Stack spacing={1.5}>
        {held.length === 0 ? (
          <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 12 }}>
            Nobody linked yet.
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {held.map((view) => (
              <Stack
                key={view.link.id}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ minWidth: 0 }}
              >
                <Typography
                  sx={{ color: 'var(--tsb-fg)', fontSize: 12.5, minWidth: 0, overflowWrap: 'anywhere' }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>{view.otherName}</Box>
                  {` — ${view.summary}`}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={() => onDelete(view.link.id)}
                  aria-label={`Unlink ${view.otherName}`}
                  sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-fg-muted)', minWidth: 0 }}
                >
                  Unlink
                </Button>
              </Stack>
            ))}
          </Stack>
        )}

        {adding ? (
          <Stack spacing={1.25}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              label="Who?"
              placeholder="Type a name"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setOtherId('');
              }}
            />
            {/*
              A plain list rather than a combobox: the host is picking from a
              roster they can see, and the name they type has to resolve to an
              id before anything is written — a free-text partner is exactly the
              "someone called Dev" that never joins up to a real guest.
            */}
            <Box
              sx={{
                maxHeight: 140,
                overflowY: 'auto',
                border: '1px solid var(--tsb-border-soft)',
                borderRadius: 'var(--tsb-radius-sm)',
              }}
            >
              {candidates.length === 0 ? (
                <Typography sx={{ p: 1, color: 'var(--tsb-fg-muted)', fontSize: 12 }}>
                  {query.trim()
                    ? `Nobody else on this trip matches “${query.trim()}”.`
                    : 'Everyone else on this trip is already linked to them.'}
                </Typography>
              ) : (
                candidates.map((candidate) => (
                  <Box
                    key={candidate.inviteeId}
                    component="button"
                    type="button"
                    onClick={() => setOtherId(candidate.inviteeId)}
                    aria-pressed={otherId === candidate.inviteeId}
                    sx={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      px: 1,
                      py: 0.75,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 12.5,
                      color: 'var(--tsb-fg)',
                      bgcolor:
                        otherId === candidate.inviteeId ? 'var(--tsb-brand-tint)' : 'transparent',
                      '&:hover': { bgcolor: 'var(--tsb-bg-elevated)' },
                      '&:focus-visible': { boxShadow: 'var(--tsb-focus-ring)', outline: 'none' },
                    }}
                  >
                    {candidate.fullName}
                  </Box>
                ))
              )}
            </Box>

            <Stack direction="row" spacing={1}>
              <TextField
                select
                size="small"
                label="They are"
                value={relation}
                onChange={(event) => {
                  const next = event.target.value as LinkRelation;
                  setRelation(next);
                  // Offered, never forced — the host can still say "cousins,
                  // keep them apart", which is the whole reason relation and
                  // seating are two fields.
                  setSeating(defaultSeatingFor(next));
                }}
                sx={{ flex: 1 }}
              >
                {LINK_RELATIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {LINK_RELATION_LABELS[option]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="At a table"
                value={seating}
                onChange={(event) => setSeating(event.target.value as LinkSeating)}
                sx={{ flex: 1 }}
              >
                {LINK_SEATINGS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {LINK_SEATING_LABELS[option]}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={busy || !otherId}
                onClick={() => {
                  void onCreate({ otherId, relation, seating }).then((ok) => {
                    if (ok) reset();
                  });
                }}
                sx={GUEST_SECONDARY_BUTTON_SX}
              >
                Link them
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={reset}
                sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-fg-muted)' }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Box>
            <Button
              variant="text"
              disabled={busy}
              onClick={() => setAdding(true)}
              sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-brand-ink)' }}
            >
              ＋ Link someone
            </Button>
          </Box>
        )}
      </Stack>
    </RecordSection>
  );
}
